import { useThemeContext } from '#contexts/ThemeContext';
import { InfoHelperIcon } from '#images/icon';
import { EAnswerItemType } from '#models/layout';
import { QuestionnaireElements } from '#models/questionnaire';
import { PictureAnswerProps } from '#modules/PictureAnswer';
import { useAnswerStyles } from '#styles/imageAnswer';
import { Box, Checkbox, Paper, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { CheckOutlined, CloseOutlined } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import * as React from 'react';
import { ImageComponent } from './ImageComponent';

export const PictureEvenAnswer = ({
  answer,
  type = EAnswerItemType.Column,
  selected,
  multipleSelection
}: PictureAnswerProps) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const elements = useThemeContext();
  const [isShowHelperText, setIsShowHelperText] = React.useState<boolean>(false);

  const { getFixedSize, getFixedSizeImage, answerText, answerBox, answerBoxSelected } = useStyles(
    elements,
    isSmDown,
    isShowHelperText,
    multipleSelection
  )();

  const classes = useAnswerStyles(isShowHelperText)();
  const handleClickAnswer = (e: React.MouseEvent) => {
    if (isShowHelperText) {
      e.stopPropagation();
    }
  };

  return (
    <Paper className={`${getFixedSize} ${answerBox} ${selected ? answerBoxSelected : ''}`}>
      <Box
        position='relative'
        height='100%'
        display='flex'
        flexDirection='column'
        onClick={(e) => handleClickAnswer(e)}
      >
        {isShowHelperText && (
          <Box
            onClick={() => setIsShowHelperText(false)}
            bgcolor={elements.primaryButtonBackground}
            width={40}
            height={40}
            position='absolute'
            display='flex'
            alignItems='center'
            justifyContent='center'
            top={-1}
            right={-1}
            borderRadius={'0 0 0 10px'}
          >
            <CloseOutlined style={{ color: elements.primaryButtonText }} />
          </Box>
        )}
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          px={2}
          pt={1}
          justifyContent='center'
          className={`${isShowHelperText ? classes.answerTextWrapper : ''}`}
          height={'80%'}
        >
          <Box mx='auto' my={0} width='100%'>
            <ImageComponent
              src={answer.imageUrl}
              color={elements.iconColor}
              className={`${isShowHelperText ? classes.pictureHelper : getFixedSizeImage}`}
              alt={answer.text}
            />
          </Box>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            mt={isShowHelperText ? 1 : 2}
            height={'calc(100% / 3)'}
            width={type === EAnswerItemType.Row ? '100%' : 'auto'}
          >
            <Typography
              variant={isShowHelperText ? (isMdDown ? 'subtitle2' : 'subtitle1') : isMdDown ? 'subtitle1' : 'h6'}
              className={`${answerText} ${classes.answerText}`}
              align='center'
            >
              {answer.text}
            </Typography>
          </Box>
          {multipleSelection && !isShowHelperText && (
            <Box mt={{ xs: 1, lg: 2 }}>
              <Checkbox
                color='primary'
                checked={selected}
                style={{ padding: '0px 0.625rem' }}
                className={classes.checkbox}
                checkedIcon={<CheckOutlined />}
              />
            </Box>
          )}
        </Box>
        {answer.helperText && !isShowHelperText && (
          <Box
            height={58}
            width='100%'
            display='flex'
            alignItems='center'
            justifyContent='center'
            onClick={(e) => {
              e.stopPropagation();
              setIsShowHelperText(true);
            }}
            className={classes.helperAnswerWrapper}
            position='absolute'
            bottom={0}
            pb={2}
          >
            <InfoHelperIcon className='helperIcon' />
          </Box>
        )}
        {answer.helperText && isShowHelperText && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={classes.helperAnswerWrapper}
            overflow='auto'
            p={2}
            pb={0}
            mb={{ xs: isShowHelperText ? 1 : 0, md: isShowHelperText ? 2 : 0 }}
          >
            <Typography variant={isMdDown ? 'caption' : 'subtitle2'} style={{ color: elements.answerHelperText }}>
              {answer.helperText}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const useStyles = (
  elements: Partial<QuestionnaireElements>,
  isSmDown: Boolean,
  isShowHelperText: boolean,
  multipleSelection: boolean
) => {
  return makeStyles((theme: any) => ({
    getFixedSize: {
      [theme.breakpoints.up('md')]: {
        width: 171,
        height: multipleSelection ? 261 : 231
      },
      [theme.breakpoints.up('lg')]: {
        width: 254,
        height: multipleSelection ? 350 : 320
      }
    },
    getFixedSizeImage: {
      borderRadius: '0.25rem',
      objectFit: 'cover',
      margin: '0 auto',
      [theme.breakpoints.up('xs')]: {
        width: 94,
        height: 94
      },
      [theme.breakpoints.up('sm')]: {
        width: 109,
        height: 109
      },
      [theme.breakpoints.up('md')]: {
        width: 96,
        height: 96
      },
      [theme.breakpoints.up('lg')]: {
        width: 130,
        height: 130
      },
      [theme.breakpoints.up('xl')]: {
        width: 130,
        height: 130
      }
    },
    answerText: {
      color: elements.answerText
    },
    icon: {
      color: elements.helperIconLink
    },
    answerBox: {
      outline: '1px solid transparent',
      cursor: 'pointer',
      '&:hover': {
        outlineColor:
          elements.isTouchDevice || isSmDown ? 'transparent' : elements.answerCardSelectedState + '!important'
      }
    },
    answerBoxSelected: {
      outlineColor: elements.answerCardSelectedState + '!important'
    }
  }));
};
